import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { smsSettingsService } from '../../lib/sms-settings-service';
import { twilioService } from '../../lib/twilio-service';
import { MessageSquare, Bell, Clock, Save, AlertCircle, Check, Phone } from 'lucide-react';

export default function SMSSettings() {
  const { currentUser, family } = useFamilyContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSMS, setTestingSMS] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [settings, setSettings] = useState({
    phone_number: '',
    is_enabled: false,
    notification_types: ["daily_reminders", "family_activity_voting", "confirmed_family_plans", "new_idea", "family_time_invite"],
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    timezone: 'America/New_York'
  });

  useEffect(() => {
    loadSettings();
  }, [currentUser, family]);

  const loadSettings = async () => {
    if (!currentUser || !family) return;
    
    try {
      setLoading(true);
      const prefs = await smsSettingsService.getSettings(family.id, currentUser.id);
      setSettings(prefs);
    } catch (error) {
      console.error('Error loading SMS settings:', error);
      setErrorMessage('Failed to load SMS settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setSettings(prev => ({
      ...prev,
      phone_number: value
    }));
  };

  const handleToggleEnabled = () => {
    setSettings(prev => ({
      ...prev,
      is_enabled: !prev.is_enabled
    }));
  };

  const handleNotificationTypeToggle = (notificationType) => {
    setSettings(prev => {
      const newTypes = prev.notification_types.includes(notificationType)
        ? prev.notification_types.filter(t => t !== notificationType)
        : [...prev.notification_types, notificationType];
      
      return {
        ...prev,
        notification_types: newTypes
      };
    });
  };

  const handleTestSMS = async () => {
    if (!settings.phone_number) {
      setErrorMessage('Please enter a phone number first');
      return;
    }

    setTestingSMS(true);
    setErrorMessage('');
    
    try {
      const formattedNumber = twilioService.formatPhoneNumber(settings.phone_number);
      
      if (!twilioService.validatePhoneNumber(formattedNumber)) {
        setErrorMessage('Please enter a valid phone number');
        return;
      }

      await twilioService.sendSMS(
        formattedNumber,
        twilioService.templates.testMessage(),
        currentUser.id,
        'test'
      );
      
      setSuccessMessage('Test message sent! Check your phone.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error sending test SMS:', error);
      setErrorMessage('Failed to send test message');
    } finally {
      setTestingSMS(false);
    }
  };

  const handleSave = async () => {
    if (!family) {
      setErrorMessage('Family context required');
      return;
    }

    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Format and validate phone number if provided
      if (settings.phone_number) {
        const formattedNumber = twilioService.formatPhoneNumber(settings.phone_number);
        
        if (!twilioService.validatePhoneNumber(formattedNumber)) {
          setErrorMessage('Please enter a valid phone number');
          setSaving(false);
          return;
        }
        
        settings.phone_number = formattedNumber;
      }

      await smsSettingsService.updateSettings(family.id, currentUser.id, settings);
      setSuccessMessage('SMS settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error saving SMS settings:', error);
      setErrorMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-neutral-50 rounded-xl shadow-sm border border-stone-200">
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-indigo-600" />
            <h2 className="text-2xl font-semibold text-stone-900">SMS Notifications</h2>
          </div>
          <p className="mt-2 text-stone-600">
            Manage your SMS notification preferences
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Success/Error Messages */}
          {successMessage && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-2">
              <Check className="w-5 h-5 text-indigo-800" />
              <span className="text-emerald-800">{successMessage}</span>
            </div>
          )}
          
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-800">{errorMessage}</span>
            </div>
          )}

          {/* Phone Number Setup */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-stone-900">Phone Number</h3>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={settings.phone_number || ''}
                  onChange={handlePhoneChange}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <button
                onClick={handleTestSMS}
                disabled={!settings.phone_number || testingSMS}
                className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                {testingSMS ? 'Sending...' : 'Test SMS'}
              </button>
            </div>
          </div>

          {/* Master Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
            <div>
              <h3 className="text-lg font-medium text-stone-900">Enable SMS Notifications</h3>
              <p className="text-sm text-stone-600">Master switch for all SMS notifications</p>
            </div>
            <button
              onClick={handleToggleEnabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.is_enabled ? 'bg-indigo-600' : 'bg-stone-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-neutral-50 transition-transform ${
                  settings.is_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {settings.is_enabled && (
            <>
              {/* Notification Types */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-stone-900 flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Types
                </h3>
                
                <div className="space-y-3">
                  {[
                    { key: "daily_reminders", label: "Daily Reminders" },
                    { key: "family_activity_voting", label: "Family Event Voting" },
                    { key: "confirmed_family_plans", label: "Confirmed Family Plans" },
                    { key: "new_idea", label: "New Ideas" },
                    { key: "family_time_invite", label: "1:1 Invites" }
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-3 border border-stone-200 rounded-lg">
                      <label className="text-sm font-medium text-stone-700">
                        {label}
                      </label>
                      <input
                        type="checkbox"
                        checked={settings.notification_types.includes(key)}
                        onChange={() => handleNotificationTypeToggle(key)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-stone-300 rounded"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Quiet Hours */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-stone-900 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Quiet Hours
                </h3>
                <p className="text-sm text-stone-600">Set times when you don't want to receive SMS notifications</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={settings.quiet_hours_start}
                      onChange={(e) => setSettings(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={settings.quiet_hours_end}
                      onChange={(e) => setSettings(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-neutral-50 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-200 text-sm text-stone-600">
          <p>Standard messaging rates apply. Reply STOP to any message to unsubscribe from all SMS notifications.</p>
        </div>
      </div>
    </div>
  );
}